import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { URL as API_URL } from "../store/utils";

const AuthContext = createContext({});

// API base URL'i — dev'de env'den, prod'da same-origin.
const _resolveApiBase = () => {
  const isDev = process.env.NODE_ENV === "development";
  if (isDev) {
    return String(API_URL.url || API_URL.url1 || "").replace(/\/+$/, "");
  }
  return typeof window !== "undefined" ? window.location.origin : "";
};

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => {
    const auth = localStorage.getItem("auth");
    return auth ? JSON.parse(auth) : false;
  });

  // auth degistiginde localStorage'a yaz
  useEffect(() => {
    localStorage.setItem("auth", JSON.stringify(auth));
  }, [auth]);

  // Logout fonksiyonu
  const logout = useCallback(() => {
    setAuth(false);
    localStorage.removeItem("auth");
    // HashRouter kullaniyoruz, ana sayfaya yonlendir
    window.location.hash = "#/";
  }, []);

  // session-expired event dinle (baseQueryWithReauth'dan gelir)
  useEffect(() => {
    const handleSessionExpired = (event) => {
      const reason = event?.detail?.reason;
      console.warn(`Session expired: ${reason || "unknown"}`);
      logout();
    };

    window.addEventListener("session-expired", handleSessionExpired);
    return () => {
      window.removeEventListener("session-expired", handleSessionExpired);
    };
  }, [logout]);

  // ── /api/auth/me auto-refresh ───────────────────────────────────
  // Sayfa yenilendiğinde localStorage'dan okunan auth state'teki
  // permission snapshot'ı eski olabilir (ör. yöneticinin rol şablonunu
  // güncelleme sonrası). Mount'ta ve window focus'unda taze veri çek.
  const tokenRef = useRef(null);
  tokenRef.current = auth?.accessToken || null;

  const refreshMe = useCallback(async () => {
    const token = tokenRef.current;
    if (!token) return;
    try {
      const base = _resolveApiBase();
      const res = await fetch(`${base}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.warn("[Auth] /me refresh non-OK status:", res.status);
        return;
      }
      const body = await res.json();
      const fresh = body?.data?.user;
      if (!fresh) return;
      // eslint-disable-next-line no-console
      console.log("[Auth] /me refresh OK", {
        role: fresh.role,
        permissionsCount: Array.isArray(fresh.permissions) ? fresh.permissions.length : 0,
        sample: Array.isArray(fresh.permissions) ? fresh.permissions.slice(0, 10) : null,
      });
      setAuth((prev) => (prev ? { ...prev, ...fresh, accessToken: token } : prev));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[Auth] /me refresh failed:", err?.message);
    }
  }, []);

  // Mount'ta bir kez çalıştır (sayfa yenileme sonrası)
  useEffect(() => {
    refreshMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Window tab'a geri geldiğinde (focus) taze çek — başka kullanıcı
  // template'i güncellediğinde sonraki focus'ta menü hemen yenilenir.
  useEffect(() => {
    const handler = () => refreshMe();
    window.addEventListener("focus", handler);
    return () => window.removeEventListener("focus", handler);
  }, [refreshMe]);

  return (
    <AuthContext.Provider value={{ auth, setAuth, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
